import { cloneDeep, each, filter, find, some } from 'lodash';
import AppService from 'App/legacy/AppService';
import Product from 'App/resources/Model/Product';
import ProductCategory from 'App/resources/Model/ProductCategory';

// Nodes are built by mutating copies of the AppService data, so everything added on the way is
// optional until the tree is finished.
export type TreeCategory = Omit<ProductCategory, 'id'> & {
	id?: number | string;
	parent?: { id: number } | null;
	$id?: number;
	$depth?: number;
	$$length?: number;
	$$parent?: TreeCategory;
	isRole?: true;
	children?: TreeNode[];
};

export type TreeProduct = Product & {
	$depth?: number;
	$$parent?: TreeCategory;
	isRole?: false;
	children?: undefined;
};

export type TreeNode = TreeCategory | TreeProduct;

const setDepth = (nodes: TreeNode[], depth: number) => {
	each(nodes, node => {
		node.$depth = depth;
		if (node.children) {
			setDepth(node.children, depth + 1);
		}
	});
};

const countChildren = (nodes: TreeNode[]) => {
	const helper = (group: TreeCategory) => {
		let counter = 0;
		each(group.children ?? [], child => {
			if (!child.isRole) {
				counter++;
			} else {
				counter += helper(child);
			}
		});
		group.$$length = counter;
		return counter;
	};

	each(nodes, node => {
		if (node.children) {
			helper(node);
		}
	});
};

export const buildProductTree = (
	onlyActive?: boolean,
	skipAuth?: boolean,
	selectableCategories?: boolean,
	usePriceLists?: boolean,
	excludeBundles?: boolean
): { data: TreeNode[] } => {
	let categories: TreeCategory[] = cloneDeep(AppService.getProductCategories(skipAuth));
	const products: TreeProduct[] = cloneDeep(
		AppService.getProducts(onlyActive, skipAuth, usePriceLists, excludeBundles)
	);
	const root: TreeNode[] = [];

	each(products, product => {
		// If root
		if (!product.category || product.category.id === 0) {
			product.$depth = 0;
			root.push(product);
		} else {
			const parent = find(categories, { id: product.category.id });

			if (parent) {
				if (!parent.children) {
					parent.children = [];
				}
				parent.children.push(product);
				product.$$parent = parent;
			}
		}
	});

	// Filter out roles without users or child roles.
	categories = filter(categories, category => {
		if (category.children) {
			return true;
		}
		return some(categories, innerCategory => innerCategory.parent && innerCategory.parent.id === category.id);
	});

	each(categories, category => {
		// If root
		if (!category.parent || category.parent.id === 0) {
			category.$depth = 0;
			root.push(category);
		} else {
			const parent = find(categories, cat => category.parent!.id === cat.id || category.parent!.id === cat.$id);

			if (parent) {
				if (!parent.children) {
					parent.children = [];
				}
				parent.children.push(category);
				category.$$parent = parent;
			}
		}
		category.isRole = true;
		category.$id = category.id as number;
		if (!category.children) {
			category.children = [];
		}
		if (selectableCategories) {
			category.id = 'category-' + category.$id;
		} else {
			// don't want select2 to be able to select a optgroup
			delete category.id;
		}
	});

	setDepth(root, 0);
	countChildren(root);

	return { data: root };
};
